import React from "react";
import { useTranslation } from "react-i18next";
import "./CSS/QueueCardsGrid.css";

/**
 * Affiche une grille de cartes (1 par queue) avec 3 KPIs par carte :
 *  - Queued = todayCalls (cumul appels queued aujourd'hui)
 *  - Live   = callsInQueue (appels actifs maintenant — même champ que la widget "Live" globale)
 *  - Agents = agentsReady, avec un exposant = total - ready (indispo)
 *
 * Layout : grid auto-fill → les cartes s'arrangent en lignes selon la largeur dispo.
 * Real-time : queueKpis vient de QueuesComponent qui s'abonne aux events SignalR
 * (un event par queue). À chaque update, setQueueKpis re-render → la card est à jour.
 */
const QueueCardsGrid = ({ queues = [], queueKpis = {}, selectedQueueId = null, onSelectQueue }) => {
    const { t } = useTranslation();

    const computeAgents = (kpi) => {
        const ready = kpi?.agentsReady || 0;
        let total = 0;
        if (kpi?.agentDetails && typeof kpi.agentDetails === "object") {
            total = Object.keys(kpi.agentDetails).length;
        }
        return { ready, unavailable: Math.max(0, total - ready) };
    };

    if (!queues || queues.length === 0) {
        return (
            <div className="queue-cards-grid-empty">
                {t("REALTIME.NO_QUEUE_DATA") || "No queues available"}
            </div>
        );
    }

    return (
        <div className="queue-cards-grid">
            {queues.map((queue) => {
                const queueId = queue.id || queue.RowKey;
                const kpi = queueKpis[queueId];
                const queueName = queue.queueName || queue.name || queueId;
                const queued = kpi?.todayCalls ?? 0;
                const live = kpi?.callsInQueue ?? 0; // même champ que la widget "Live" globale
                const { ready, unavailable } = computeAgents(kpi);

                const isSelected = selectedQueueId === queueId;
                return (
                    <div
                        key={queueId}
                        className={`qcg-card ${isSelected ? 'qcg-card-selected' : ''}`}
                        onClick={() => onSelectQueue && onSelectQueue(queueId)}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="qcg-card-title" title={queueName}>{queueName}</div>
                        <div className="qcg-card-metrics">
                            <div className="qcg-metric" title={t("REALTIME.QUEUED_TODAY_TOOLTIP", "Total calls queued today")}>
                                <div className="qcg-metric-label">{t("REALTIME.QUEUED_TODAY", "Today's calls")}</div>
                                <div className="qcg-metric-value">{queued}</div>
                            </div>
                            <div className="qcg-metric" title={t("REALTIME.WAITING_NOW_TOOLTIP", "Calls currently waiting in queue")}>
                                <div className="qcg-metric-label">{t("REALTIME.WAITING_NOW", "Waiting")}</div>
                                <div className="qcg-metric-value">{live}</div>
                            </div>
                            <div className="qcg-metric" title={t("REALTIME.AGENTS_TOOLTIP", "Ready agents (superscript: unavailable)")}>
                                <div className="qcg-metric-label">{t("REALTIME.AGENTS", "Agents")}</div>
                                <div className="qcg-metric-value">
                                    {ready}
                                    {unavailable > 0 && <sup className="qcg-metric-sup">{unavailable}</sup>}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default QueueCardsGrid;
